import React, {type ReactNode, createContext, useContext, useMemo} from 'react';
import {gql, useQuery} from '@apollo/client';
import {createFeatureFlagsClient, FeatureFlagsProvider} from '@ezcater/feature-flags-react';
import type {DeliveryManagementCurrentAccountStoresQuery} from '../../__generated__/DeliveryManagementCurrentAccountStoresQuery';
import Loader from '../Loader';
import {Layout, MainContent} from '../Layout';
import {signInUrlWithDestUrl} from '../../paths';
import {useEnvironment} from '../Environment';

type Store = {
  uuid: string;
  name: string;
  address: {street: string} | null;
};

type CurrentAccountStoresType = {
  stores: Store[];
};

export const CurrentAccountStoresContext = createContext<CurrentAccountStoresType>({
  stores: [],
});

export const CurrentAccountStoresQuery = gql`
  query DeliveryManagementCurrentAccountStoresQuery {
    me {
      id
      catererAccount {
        id
        stores {
          nodes {
            id
            uuid
            name
            deliveryManagementEnabled
            address {
              id
              street
            }
          }
        }
      }
    }
  }
`;

const performLoginRedirect = () => {
  if (typeof window !== 'undefined') {
    window.location.assign(signInUrlWithDestUrl(window.location.href));
  }
};

type CurrentAccountStoresProviderProps = {
  children: ReactNode;
};

export const CurrentAccountStoresProvider = ({children}: CurrentAccountStoresProviderProps) => {
  const currentAccountStoresQueryResult = useQuery<DeliveryManagementCurrentAccountStoresQuery>(
    CurrentAccountStoresQuery,
    {
      errorPolicy: 'all',
      returnPartialData: true,
      onCompleted: result => {
        if (!result.me?.catererAccount?.id) {
          performLoginRedirect();
        }
      },
      onError: () => {
        performLoginRedirect();
      },
    }
  );

  const catererAccount = currentAccountStoresQueryResult.data?.me?.catererAccount;
  const stores = (catererAccount?.stores?.nodes || []).reduce<Store[]>((acc, node) => {
    if (node?.deliveryManagementEnabled) {
      acc.push(node);
    }

    return acc;
  }, []);

  const {featureFlagHost} = useEnvironment();
  const featureFlagsClient = useMemo(
    () =>
      createFeatureFlagsClient({
        host: featureFlagHost,
      }),
    [featureFlagHost]
  );

  return (
    <CurrentAccountStoresContext.Provider value={{stores}}>
      <Layout>
        <MainContent>
          {currentAccountStoresQueryResult.loading || !catererAccount?.id ? (
            <Loader inline={false} />
          ) : (
            <FeatureFlagsProvider client={featureFlagsClient} trackingId={catererAccount?.id}>
              {children}
            </FeatureFlagsProvider>
          )}
        </MainContent>
      </Layout>
    </CurrentAccountStoresContext.Provider>
  );
};

export const useCurrentAccountStores = () => {
  return useContext(CurrentAccountStoresContext);
};
